"use client";

import React, { useEffect, useState } from "react";
import {
  CART_CHANGE_EVENT,
  CART_STORAGE_KEYS,
  getLatestCartWithStorageKey
} from "../../molecules/cart-side-sheet/cart-storage";
import { CartIcon } from "./icons";
import { reportNavPanelOpen } from "./nav-open-report";
import styles from "./global-nav.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** The count belongs in the accessible name — the badge alone is visual. */
export function badgeLabel(label: string, count?: number) {
  if (count === undefined) {
    return label;
  }
  if (count <= 0) {
    return `${label}, empty`;
  }
  return `${label}, ${count} ${count === 1 ? "item" : "items"}`;
}

/* One saved order is one item, the same cart the side sheet opens. No cart
   leaves the count unset rather than 0, so the name stays the bare label. */
const readSavedCartCount = () => (getLatestCartWithStorageKey() ? 1 : undefined);

/** The saved-cart count, read after mount so the server render and the first
    client render agree. Re-read when another tab writes storage, when this page
    announces a write, and when the page returns from the back-forward cache. */
function useSavedCartCount(enabled: boolean) {
  const [count, setCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const read = () => setCount(readSavedCartCount());
    /* Other tabs write storage for many reasons; only a cart slot, or a full
       clear (`key` null), can change the count. */
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || (CART_STORAGE_KEYS as readonly string[]).includes(event.key)) {
        read();
      }
    };
    read();
    window.addEventListener("storage", onStorage);
    window.addEventListener("pageshow", read);
    window.addEventListener(CART_CHANGE_EVENT, read);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", read);
      window.removeEventListener(CART_CHANGE_EVENT, read);
    };
  }, [enabled]);

  return count;
}

export interface NavCartTriggerProps {
  /** id of the popover element the button opens. */
  panelId: string;
  label: string;
  /** A fixed count. Absent, the count comes from the shopper's saved cart. */
  badgeCount?: number;
  /** Placement the click reports under. Absent, the click reports nothing. */
  linkPosition?: string;
}

/** The reported name. Not the accessible name, which carries the count. */
export const CART_TRIGGER_LINK_NAME = "cart";

/** The path segment after the page URL; the cart sheet's own page view appends the same one. */
const CART_PANEL = "panel_cart";

function NavCartTriggerImpl({ panelId, label, badgeCount, linkPosition }: NavCartTriggerProps) {
  const saved = useSavedCartCount(badgeCount === undefined);
  const count = badgeCount ?? saved;

  /* ⚠ No `data-link*` on the button: the container would name the click from
     the `aria-label` ("Cart, 1 item") and report it twice. The modal sheet's
     inert backdrop covers the trigger, so every click reaching it opens. */
  const report = () => {
    if (linkPosition) {
      reportNavPanelOpen({
        linkName: CART_TRIGGER_LINK_NAME,
        linkPosition,
        panel: CART_PANEL
      });
    }
  };

  return (
    <button
      aria-label={badgeLabel(label, count)}
      className={styles.iconButton}
      onClick={report}
      /* camelCase on purpose — see panelTrigger in global-nav.tsx. */
      popoverTarget={panelId}
      type="button"
    >
      <CartIcon />
      {(count ?? 0) > 0 && (
        <span aria-hidden="true" className={styles.badge}>
          {count}
        </span>
      )}
    </button>
  );
}
export const NavCartTrigger = withSafeRender(NavCartTriggerImpl, "NavCartTrigger");
